<template>
  <div class="flex h-svh w-full overflow-hidden bg-sidebar">
    <!-- Sidebar mobile -->
    <TransitionRoot as="template" :show="sidebarOpen">
      <Dialog as="div" class="relative z-40 md:hidden" @close="sidebarOpen = false">
        <TransitionChild as="template" enter="transition-opacity ease-linear duration-200" enter-from="opacity-0"
                         enter-to="opacity-100" leave="transition-opacity ease-linear duration-150"
                         leave-from="opacity-100" leave-to="opacity-0">
          <div class="fixed inset-0 bg-black/50"/>
        </TransitionChild>
        <div class="fixed inset-0 z-40 flex">
          <TransitionChild as="template" enter="transition ease-in-out duration-200 transform"
                           enter-from="-translate-x-full" enter-to="translate-x-0"
                           leave="transition ease-in-out duration-150 transform" leave-from="translate-x-0"
                           leave-to="-translate-x-full">
            <DialogPanel class="relative flex w-full max-w-xs flex-1 flex-col bg-sidebar">
              <div class="flex h-14 items-center px-4">
                <img class="h-7 w-auto" src="https://onetsolutions.net/assets/img/brand/logo/logo.png" alt="Clafoutis"/>
              </div>
              <div class="px-3 pb-2 pt-1">
                <select
                    v-model="selectedFiscalYearId"
                    class="h-9 w-full rounded-md border border-sidebar-border bg-sidebar-accent px-2 text-sm text-sidebar-accent-foreground focus:outline-none"
                    @change="changeFiscalYear">
                  <option v-for="year in fiscalYearData" :key="year.id" :value="year.id" class="text-foreground">{{ year.name }}</option>
                </select>
              </div>
              <nav class="flex-1 overflow-y-auto px-3 pb-2 pt-2">
                <SidebarContent :navigation="navigation" :current-path="$route.path" @navigate="sidebarOpen = false"/>
              </nav>
            </DialogPanel>
          </TransitionChild>
          <div class="w-14 flex-shrink-0"/>
        </div>
      </Dialog>
    </TransitionRoot>

    <!-- Sidebar desktop (transparent, floating) -->
    <div :class="['hidden h-svh shrink-0 flex-col transition-[width] duration-200 md:flex',
                  sidebarCollapsed ? 'w-16' : 'w-64']">
      <div class="flex h-16 shrink-0 items-center justify-center border-b border-sidebar-border px-2">
        <router-link to="/" class="flex items-center">
          <img class="h-7 w-auto max-w-full object-contain" src="https://onetsolutions.net/assets/img/brand/logo/logo.png" alt="Clafoutis"/>
        </router-link>
      </div>
      <div v-if="!sidebarCollapsed" class="px-3 pb-2 pt-4">
        <label class="mb-1 block px-1 text-[11px] font-semibold uppercase tracking-wider text-sidebar-muted">Exercice</label>
        <select
            v-model="selectedFiscalYearId"
            class="flex h-9 w-full items-center rounded-md border border-sidebar-border bg-sidebar-accent px-2 text-sm text-sidebar-accent-foreground focus:outline-none focus:ring-1 focus:ring-sidebar-border"
            @change="changeFiscalYear">
          <option v-for="year in fiscalYearData" :key="year.id" :value="year.id" class="text-foreground">{{ year.name }}</option>
        </select>
      </div>
      <div v-else class="flex justify-center px-2 pb-2 pt-4">
        <button type="button"
                class="flex h-9 w-9 items-center justify-center rounded-md border border-sidebar-border bg-sidebar-accent text-[11px] font-semibold text-sidebar-accent-foreground hover:bg-sidebar-accent"
                :title="`Exercice ${currentFiscalYearName}`"
                @click="sidebarCollapsed = false">
          {{ shortFiscalYearName }}
        </button>
      </div>
      <nav :class="['flex-1 overflow-y-auto pb-2 pt-2', sidebarCollapsed ? 'px-2' : 'px-3']">
        <SidebarContent :navigation="navigation" :current-path="$route.path" :collapsed="sidebarCollapsed"
                        @expand="sidebarCollapsed = false"/>
      </nav>
      <div class="shrink-0 p-2">
        <Menu as="div" class="relative">
          <MenuButton :class="['flex w-full items-center rounded-md p-2 text-left hover:bg-sidebar-accent',
                               sidebarCollapsed ? 'justify-center' : 'gap-2']">
            <img class="h-8 w-8 rounded-lg" :src="userData.gravatar" :title="sidebarCollapsed ? userData.name : null" alt=""/>
            <span v-if="!sidebarCollapsed" class="min-w-0 flex-1">
              <span class="block truncate text-sm font-medium text-sidebar-accent-foreground">{{ userData.name }}</span>
              <span class="block truncate text-xs text-sidebar-foreground">{{ userData.email }}</span>
            </span>
            <ChevronDownIcon v-if="!sidebarCollapsed" class="h-4 w-4 text-sidebar-foreground"/>
          </MenuButton>
          <transition enter-active-class="transition ease-out duration-100"
                      enter-from-class="transform opacity-0 scale-95"
                      enter-to-class="transform opacity-100 scale-100"
                      leave-active-class="transition ease-in duration-75"
                      leave-from-class="transform opacity-100 scale-100"
                      leave-to-class="transform opacity-0 scale-95">
            <MenuItems
                class="absolute bottom-14 left-0 z-30 w-56 rounded-lg border border-border bg-popover py-1 shadow-lg focus:outline-none">
              <MenuItem v-for="item in userNavigation" :key="item.name" v-slot="{ active }">
                <router-link :to="item.href"
                             :class="[active ? 'bg-accent' : '', 'block px-4 py-2 text-sm text-popover-foreground']">
                  {{ item.name }}
                </router-link>
              </MenuItem>
              <MenuItem v-for="item in secondaryNavigation" :key="item.name" v-slot="{ active }">
                <a :href="item.href" target="_blank"
                   :class="[active ? 'bg-accent' : '', 'block px-4 py-2 text-sm text-popover-foreground']">
                  {{ item.name }}
                </a>
              </MenuItem>
              <MenuItem v-slot="{ active }">
                <button type="button" @click="logout"
                        :class="[active ? 'bg-accent' : '', 'block w-full px-4 py-2 text-left text-sm text-destructive']">
                  Déconnexion
                </button>
              </MenuItem>
            </MenuItems>
          </transition>
        </Menu>
      </div>
    </div>

    <!-- Inset content card (floating white rounded panel) -->
    <div
        class="relative flex h-svh flex-1 flex-col overflow-hidden bg-background md:my-2 md:mr-2 md:h-[calc(100svh-1rem)] md:rounded-xl md:shadow-sm">
      <header
          class="sticky top-0 z-20 flex h-14 shrink-0 items-center gap-3 border-b border-border bg-background/60 px-4 backdrop-blur-md md:rounded-t-xl">
        <button type="button"
                class="-ml-2 rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground md:hidden"
                @click="sidebarOpen = true">
          <MenuAlt2Icon class="h-5 w-5"/>
        </button>
        <button type="button"
                class="-ml-2 hidden rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground md:block"
                :title="sidebarCollapsed ? 'Déplier le menu' : 'Replier le menu'"
                @click="sidebarCollapsed = !sidebarCollapsed">
          <ChevronDoubleRightIcon v-if="sidebarCollapsed" class="h-5 w-5"/>
          <ChevronDoubleLeftIcon v-else class="h-5 w-5"/>
        </button>

        <nav class="flex min-w-0 items-center gap-1.5 text-sm">
          <template v-for="(crumb, index) in breadcrumbs" :key="index">
            <ChevronRightIcon v-if="index > 0" class="h-3.5 w-3.5 shrink-0 text-muted-foreground"/>
            <router-link v-if="crumb.to && index < breadcrumbs.length - 1" :to="crumb.to"
                         class="truncate text-muted-foreground transition-colors hover:text-foreground">
              {{ crumb.label }}
            </router-link>
            <span v-else class="truncate font-medium text-foreground">{{ crumb.label }}</span>
          </template>
        </nav>

        <div class="ml-auto flex items-center gap-2">
          <button type="button"
                  class="rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  @click="showNotification = !showNotification">
            <BellIcon class="h-5 w-5"/>
          </button>
          <img class="h-7 w-7 rounded-full md:hidden" :src="userData.gravatar" alt=""/>
        </div>
      </header>

      <div class="app-content flex flex-1 items-stretch overflow-hidden">
        <main class="flex-1 overflow-y-auto p-4">
          <router-view/>
        </main>
        <TransitionRoot :show="showNotification" @close="showNotification = false">
          <aside class="hidden w-80 overflow-y-auto border-l border-border bg-card p-5 lg:block">
            <h3 class="mb-2 text-sm font-semibold">Activité</h3>
            <ul role="list" class="divide-y divide-border">
              <template v-for="activityItem in activityData" :key="activityItem.id">
                <li class="py-4" v-if="activityItem.log_name != 'default'">
                  <div class="flex space-x-3">
                    <img class="h-6 w-6 rounded-full"
                         :src="activityItem.user ? getGravatar(activityItem.user.email) : 'https://www.gravatar.com/avatar/'"
                         alt="">
                    <div class="flex-1 space-y-1">
                      <div class="flex items-center justify-between">
                        <h3 class="text-sm font-medium">
                          {{ activityItem.user ? activityItem.user.name : 'Robot' }}
                        </h3>
                        <p class="text-sm text-muted-foreground">{{ activityItem.created_at }}</p>
                      </div>
                      <p class="text-sm text-muted-foreground">
                        {{ getTranslation(activityItem.description) }} {{ getTranslation(activityItem.log_name) }}
                        <router-link :to="`/${activityItem.log_name}/${activityItem.subject_id}`" class="hover:underline">
                          ({{ activityItem.subject_id.substr(0, 7) }})
                        </router-link>
                      </p>
                    </div>
                  </div>
                </li>
              </template>
            </ul>
          </aside>
        </TransitionRoot>
      </div>
    </div>
  </div>
</template>

<script setup>
import {onBeforeUnmount, onMounted, ref} from 'vue'
import {
  Dialog,
  DialogPanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'
import {
  BellIcon,
  MenuAlt2Icon,
} from '@heroicons/vue/outline'
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/vue/solid'
import SidebarContent from '@/components/layout/SidebarContent.vue'

const secondaryNavigation = [
  {name: 'Metabase', href: 'https://metabase.clafoutis.eu/'},
  {name: 'WHMCS', href: 'https://customer.onetsolutions.net/administration/'},
  {name: 'Trello', href: 'https://trello.com/b/SG9kpaMI/onetsolutions'}
]

const userNavigation = [
  {name: 'Profile', href: '/profile'}
]

const sidebarOpen = ref(false)

// En dessous de cette largeur la sidebar passe en mode "rail" (icônes seules).
const COLLAPSE_BREAKPOINT = 1300
const sidebarCollapsed = ref(false)
let collapseQuery = null
const syncCollapsed = (event) => {
  sidebarCollapsed.value = event.matches
}

onMounted(() => {
  collapseQuery = window.matchMedia(`(max-width: ${COLLAPSE_BREAKPOINT - 1}px)`)
  sidebarCollapsed.value = collapseQuery.matches
  collapseQuery.addEventListener('change', syncCollapsed)
})

onBeforeUnmount(() => {
  collapseQuery?.removeEventListener('change', syncCollapsed)
})
</script>

<script>

import axios from "axios";
import md5 from 'crypto-js/md5';
import {
  format,
} from "date-fns";
import {
  ArchiveIcon,
  CalendarIcon,
  ChartBarIcon,
  FolderIcon,
  HomeIcon,
  InboxIcon,
  UsersIcon,
  ViewBoardsIcon,
  SupportIcon,
} from '@heroicons/vue/outline'

const BREADCRUMB_SECTIONS = {
  '': 'Dashboard',
  board: 'Board',
  inventory: 'Inventaire',
  invoice: 'Factures',
  transaction: 'Transactions',
  company: 'Fournisseurs',
  tva: 'TVA',
  tresorerie: 'Trésorerie',
  pilotage: 'Pilotage',
  support: 'Support',
  global_transaction: 'Transactions',
  profile: 'Profil',
  account: 'Compte',
}

const BREADCRUMB_PAGES = {
  backlog: 'Backlog',
  linked: 'Associées',
  done: 'Terminées',
  issue: 'Problèmes',
  timeline: 'Timeline',
  deleted: 'Supprimées',
  export: 'Export',
  status: 'Statut',
  tickets: 'Tickets',
  paypal: 'Paypal',
  stripe: 'Stripe',
  dedipass: 'Dedipass',
  xmoney: 'xMoney',
  nowpayments: 'Nowpayments',
  enterprise_europe: 'Entreprise - Europe',
  except_europe: 'Hors Europe',
  day: 'Journalier',
}

export default {
  data: function () {
    return {
      endpoint: 'user',
      userData: {},
      activityData: [],
      polling: null,
      showNotification: false,
      user: this.$auth.getUser(),
      token: null,
      invoiceCountData: 0,
      transactionCountData: 0,
      ticketsAwaitingCount: 0,
      boardUnreadCount: 0,
      navigation: [],
      fiscalYearData: [],
      selectedFiscalYearId: null,
    };
  },

  computed: {
    currentFiscalYearName() {
      const year = this.fiscalYearData.find((item) => item.id === this.selectedFiscalYearId)
      return year ? year.name : ''
    },
    shortFiscalYearName() {
      const name = this.currentFiscalYearName
      const match = name.match(/\d{4}/g)
      if (match) return match[match.length - 1]
      return name.slice(0, 4)
    },
    breadcrumbs() {
      const segments = this.$route.path.split('/').filter(Boolean)
      const crumbs = [{label: 'Clafoutis', to: '/'}]
      if (!segments.length) {
        crumbs.push({label: 'Dashboard'})
        return crumbs
      }
      const section = segments[0]
      crumbs.push({
        label: BREADCRUMB_SECTIONS[section] || section,
        to: `/${section}${section === 'invoice' || section === 'transaction' ? '/backlog' : ''}`,
      })
      if (segments[1]) {
        crumbs.push({label: BREADCRUMB_PAGES[segments[1]] || segments[1]})
      }
      return crumbs
    },
  },

  mounted() {
    this.fetchUserData()
    this.fetchActivityData()
    this.fetchInvoiceCountData()
    this.fetchTransactionCountData()
    this.fetchTicketsAwaitingCount()
    this.fetchBoardUnreadCount()
    this.fetchFiscalYears()
    this.navigationBar()
    this.intervalFetchData()
  },
  beforeUnmount() {
    clearInterval(this.polling)
  },
  methods: {

    navigationBar() {
      this.navigation = [
        {
          group: 'Général',
          items: [
            {name: 'Dashboard', href: '/', icon: HomeIcon},
            {name: 'Board', href: '/board', icon: ViewBoardsIcon, count: this.boardUnreadCount},
          ],
        },
        {
          group: 'Comptabilité',
          items: [
            {name: 'Factures', href: '/invoice/backlog', icon: UsersIcon, count: this.invoiceCountData},
            {name: 'Transactions', href: '/transaction/backlog', icon: FolderIcon, count: this.transactionCountData},
            {name: 'Fournisseurs', href: '/company', icon: CalendarIcon},
            {name: 'Inventaire', href: '/inventory', icon: ArchiveIcon},
            {name: 'Finances', href: '/pilotage', icon: ChartBarIcon},
          ],
        },
        {
          group: 'Clients',
          items: [
            {name: 'Transactions', href: '/global_transaction', icon: InboxIcon, excludePrefix: '/global_transaction/day'},
            {name: 'Journalier', href: '/global_transaction/day/' + format(new Date(), 'yyyy-MM-dd'), icon: CalendarIcon, matchPrefix: '/global_transaction/day'},
            {name: 'Support', href: '/support/tickets', icon: SupportIcon, count: this.ticketsAwaitingCount},
          ],
        },
      ]
    },
    logout() {
      this.$auth.logout();
    },
    login() {
      this.$router.push('/login');
    },
    intervalFetchData: function () {
      this.polling = setInterval(() => {
        this.fetchInvoiceCountData()
        this.fetchTransactionCountData()
        this.fetchTicketsAwaitingCount()
        this.fetchBoardUnreadCount()
        this.fetchActivityData();
        this.navigationBar()
      }, 20000);
    },
    getGravatar(email) {
      return "https://www.gravatar.com/avatar/" + md5(email)
    },

    // Translate a word to another word
    getTranslation(word) {
      switch (word) {
        case 'invoice':
          return "facture"
        case 'created':
          return "Création"
        case 'updated':
          return "Modification"
        case 'company':
          return "fournisseur"
        case 'file':
          return "fichier"
        default:
          return word
      }

    },

    async apiClient() {
      const token = await this.$auth.getAccessTokenSilently()
      return axios.create({
        baseURL: `https://billing.clafoutis.eu/api`,
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
    },

    async fetchFiscalYears() {
      const HTTP = await this.apiClient()
      HTTP.get('fiscalyear')
          .then(response => {
            this.fiscalYearData = response.data
          })
          .catch(e => {
            console.log(e)
          })
    },

    async changeFiscalYear() {
      if (!this.selectedFiscalYearId || this.selectedFiscalYearId === this.userData.fiscal_year_id) return
      const HTTP = await this.apiClient()
      await HTTP.post('user', {id: this.userData.id, fiscal_year_id: this.selectedFiscalYearId})
      window.location.reload()
    },

    async fetchInvoiceCountData() {
      const HTTP = await this.apiClient()
      HTTP.get("invoice/count")
          .then(response => {
            this.invoiceCountData = response.data['backlog']
          })
          .catch(e => {
            console.log(e)
          })
    },

    async fetchTransactionCountData() {
      const HTTP = await this.apiClient()
      HTTP.get("transaction/count")
          .then(response => {
            this.transactionCountData = response.data['backlog']
          })
          .catch(e => {
            console.log(e)
          })
    },

    async fetchBoardUnreadCount() {
      const HTTP = await this.apiClient()
      HTTP.get('issues/unread-count')
          .then(response => {
            this.boardUnreadCount = response.data.count
            this.navigationBar()
          })
          .catch(e => {
            console.log(e)
          })
    },

    async fetchTicketsAwaitingCount() {
      const HTTP = await this.apiClient()

      try {
        const response = await HTTP.get('support/tickets', {
          params: {
            status: 'All Active Tickets',
            limitnum: 1000,
            limitstart: 0
          }
        })

        if (response.data.success && response.data.tickets) {
          this.ticketsAwaitingCount = response.data.tickets.filter(ticket =>
              ticket.status === 'Open' || ticket.status === 'Customer-Reply'
          ).length
        }
      } catch (e) {
        console.error('Error fetching tickets count:', e)
      }
    },

    async fetchUserData() {
      const HTTP = await this.apiClient()
      HTTP.get(this.endpoint)
          .then(response => {
            this.userData = response.data
            this.userData.gravatar = "https://www.gravatar.com/avatar/" + md5(this.userData.email)
            this.selectedFiscalYearId = this.userData.fiscal_year_id
          })
          .catch(e => {
            console.log(e)
          })
    },
    async fetchActivityData() {
      const HTTP = await this.apiClient()
      HTTP.get('activity')
          .then(response => {
            this.activityData = response.data
          })
          .catch(e => {
            console.log(e)
          })
    },
  }
}

</script>
